import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { User } from '../../../models/User';
import { Establishment } from '../../../models/Establishment';
import { MissionDetailsComponent } from '../../../pages/etablissement/missions/encour-component/mission-details/mission-details.component';
import { MissionService } from '../../../services/mission.service';
import { MatDialog } from '@angular/material/dialog';
import { SvgIconComponent } from "angular-svg-icon";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { NgClass, NgIf, NgForOf } from "@angular/common";
import { NotificationRealtimeService } from '../../../services/notification-realtime.service';
import { Subscription } from 'rxjs';
import { inject } from '@angular/core';
import { NotificationPanelComponent } from '../../../shared/components/notification-panel/notification-panel.component';
import { LayoutResponsiveService } from '../layout-responsive.service';
import { PermissionService } from '../../../services/permission.service';
import { WeeklyHoursService, HoursSubmissionSummary } from '../../../services/weekly-hours.service';
import { FlagService } from '../../../shared/services/flag.service';
import { FlagType } from '../../../shared/utils/enums';
import { HoursSubmissionDialogComponent } from '../weekly-hours/hours-submission-dialog.component';

@Component({
  selector: 'app-agency-header',
  templateUrl: './agency-header.component.html',
  styleUrl: './agency-header.component.scss',
  imports: [
    SvgIconComponent,
    MatMenu,
    NgClass,
    NgForOf,
    MatMenuItem,
    MatMenuTrigger,
    NgIf,
    NotificationPanelComponent
  ],
  standalone: true
})
export class AgencyHeaderComponent implements OnInit, OnDestroy {
  notifNotReaded: number = 0;
  establishment: Establishment | null;
  notification: any[];
  private realtimeSub?: Subscription;

  /** Correction client (P5) : info-bulle « heures à valider » à côté des notifications. */
  pendingSubmissions: HoursSubmissionSummary[] = [];
  submissionsOpen = false;
  validatingId: number | null = null;

  /** Lot I : état responsive partagé (hamburger mobile). */
  protected readonly layout = inject(LayoutResponsiveService);

  constructor(
    private authService: AuthService,
    private dialog: MatDialog,
    public router: Router,
    private missionService: MissionService,
    private realtime: NotificationRealtimeService,
    private weeklyHoursService: WeeklyHoursService,
    private flag: FlagService,
    protected permissions: PermissionService,
  ) {}

  /** Always the freshest stored user (role changes are refreshed from the server). */
  get user(): User | null {
    return this.authService.user;
  }

  get displayName(): string {
    return this.authService.displayName || 'Mon compte';
  }

  get roleLabel(): string {
    return (this.user as any)?.role_label ?? '';
  }

  get agencyName(): string {
    try {
      return JSON.parse(localStorage.getItem('agency') || '{}')?.name || 'Mon organisation';
    } catch {
      return 'Mon organisation';
    }
  }
  disconnect() {
    localStorage.clear();
    this.authService.disconnect();
  }

  ngOnInit(): void {
    // this.establishment = this.authService.establishment;
    this.notifications();
    // Live updates: prepend new notifications and bump the unread badge without a refresh.
    this.realtime.connect();
    this.realtimeSub = this.realtime.notifications$.subscribe((notif) => {
      this.notification = [notif, ...(this.notification || [])];
      this.notifNotReaded = (this.notifNotReaded || 0) + 1;
    });
    // Correction client (P5) : badge + liste des relevés d'heures à valider (info-bulle header)
    if (this.permissions.has('FINANCE_VIEW')) {
      this.loadPendingSubmissions();
    }
  }

  get showHoursBadge(): boolean {
    return this.permissions.has('FINANCE_VIEW');
  }

  loadPendingSubmissions(): void {
    this.weeklyHoursService.listSubmissions(this.authService.agencyId ?? undefined).subscribe({
      next: (data) => { this.pendingSubmissions = data ?? []; },
      error: () => { this.pendingSubmissions = []; },
    });
  }

  toggleSubmissions(event: MouseEvent): void {
    event.stopPropagation();
    this.submissionsOpen = !this.submissionsOpen;
    if (this.submissionsOpen) {
      this.loadPendingSubmissions();
    }
  }

  /** TASK-04 (AC2) : la bulle se referme au clic en dehors (tous écrans). */
  @HostListener('document:click')
  closeSubmissions(): void {
    this.submissionsOpen = false;
  }

  openSubmissionDetail(s: HoursSubmissionSummary): void {
    this.submissionsOpen = false;
    this.dialog.open(HoursSubmissionDialogComponent, {
      width: '720px',
      panelClass: 'custom-dialog-container',
      data: { missionId: s.missionId }
    }).afterClosed().subscribe(() => this.loadPendingSubmissions());
  }

  quickValidateSubmission(s: HoursSubmissionSummary, event: MouseEvent): void {
    event.stopPropagation();
    this.validatingId = s.missionId;
    this.weeklyHoursService.validateSubmission(s.missionId).subscribe({
      next: () => {
        this.flag.displayFlag('Relevé d\'heures validé', FlagType.success);
        this.validatingId = null;
        this.loadPendingSubmissions();
      },
      error: () => {
        this.flag.displayFlag('Erreur lors de la validation', FlagType.error);
        this.validatingId = null;
      },
    });
  }

  goWeeklyHours(): void {
    this.submissionsOpen = false;
    this.router.navigate(['/agency/weekly-hours']);
  }

  ngOnDestroy(): void {
    this.realtimeSub?.unsubscribe();
    this.realtime.disconnect();
  }
  notifications() {
    this.authService.notif().subscribe({
      next: (res: any) => {
        this.notification = res;
        this.notifNotReaded = res?.filter((el: any) => !el.read).length;
      },
      error: (err) => {
        this.notification = [];
        this.notifNotReaded = 0;
      },
    });
  }

  notifClicked(notif: any) {
    this.authService
      .readNotif(notif.id, { ...notif, read: true })
      .subscribe({
        next: (res) => {
          this.notifications();
          // Extract the embedded link from content
          const linkMatch = notif.content?.match(/\[ETB_LINK:([^\]]+)\]/);
          if (linkMatch && linkMatch[1]) {
            // Navigate to the mission edit page
            this.router.navigate([linkMatch[1]]);
          } else if (/^Relevé d.heures à valider/i.test(notif.title ?? '') && notif.mission_id) {
            // Relevé d'heures envoyé par un pro : ouvre directement CE relevé (détail à valider / éditer)
            this.router.navigate(['/agency/weekly-hours'], { queryParams: { submission: notif.mission_id } });
          } else if (notif.mission_id) {
            // Mission-related notification (e.g. a candidate applied): route to the
            // candidate-management surface. Fragmentable missions are managed on a
            // dedicated page; others open the mission-details dialog (candidate tabs).
            this.openMissionCandidateView(notif.mission_id);
          } else if (/compte professionnel|profil renseigné/i.test(notif.title ?? '')) {
            // Inscription d'un pro en attente (aucune mission liée) : écran « Comptes professionnels »
            this.router.navigate(['/agency/pro-accounts']);
          }
          // Autre notification sans lien ni mission : marquée lue, rien à ouvrir (évite GET /missions/undefined)
        },
        error: (err) => {
          console.log(err);
        },
      });
  }

  /**
   * Routes a mission-related notification to the right candidate-management surface:
   * fragmentable missions are managed on the dedicated fragment page, others open the
   * mission-details dialog (which embeds the candidate tabs). The notification's
   * mission_id is always the PARENT mission id, which both surfaces expect.
   */
  private openMissionCandidateView(missionId: number) {
    this.missionService.getMissionById(String(missionId), '/v2').subscribe({
      next: (mission: any) => {
        if (mission?.isfragmentable) {
          this.router.navigate(['/agency/fragmented-mision', missionId]);
        } else {
          this.missionDetailDialog({ id: missionId });
        }
      },
      error: () => {
        // Fallback: open the dialog if the mission lookup fails
        this.missionDetailDialog({ id: missionId });
      },
    });
  }

  missionDetailDialog = (row: any) => {
    const dialog = this.dialog.open(MissionDetailsComponent, {
      width: '666px',
      height: '100vh',
      position: { right: '0' },
      backdropClass: 'bg-transparent',
      data: row,
    });
    dialog.afterClosed().subscribe((value) => {
      if (value) {
        // this.fetshMission()
      }
    });
  };

  /**
   * Strips the hidden ETB_LINK pattern from notification content for display
   */
  getDisplayContent(content: string): string {
    if (!content) return '';
    return content.replace(/\[ETB_LINK:[^\]]+\]/g, '').trim();
  }

}
